import React from 'react';
import { Link } from 'react-router-dom';
import { cn } from '../../../lib/utils';

export type ListRow = { key: string; cells: React.ReactNode[]; to?: string; ok?: boolean };

// Compact feed rows: grid-aligned cells, hairline dividers, optional status
// dot and link. Ported from splitfin-manager src/components/dash/list-rows.tsx.
export const ListRows: React.FC<{ rows: ListRow[]; cols: string; className?: string; emptyText?: string; dots?: boolean }> = ({
  rows,
  cols,
  className,
  emptyText = 'Nothing here right now',
  dots = false,
}) => {
  if (!rows.length) return <p className="py-6 text-center text-[12px] text-[color:var(--oa-muted)]">{emptyText}</p>;
  return (
    <ul className={cn('-mx-2 divide-y divide-[color:var(--oa-line-2)]', className)}>
      {rows.map((row) => {
        const cls = cn('grid items-center gap-3 rounded-lg px-2 py-2 text-[13px] text-[color:var(--oa-text)] transition-colors', row.to && 'hover:bg-[color:var(--oa-raised)]');
        const style = { gridTemplateColumns: dots ? `auto ${cols}` : cols };
        const body = (
          <>
            {dots ? <span className={cn('h-1.5 w-1.5 rounded-full', row.ok === false ? 'bg-[color:var(--oa-bad)]' : 'bg-[color:var(--oa-accent)]')} aria-label={row.ok === false ? 'needs attention' : 'ok'} /> : null}
            {row.cells.map((cell, i) => (
              <span key={i} className={cn('min-w-0 truncate', i === row.cells.length - 1 && 'text-right')}>
                {cell}
              </span>
            ))}
          </>
        );
        return (
          <li key={row.key}>
            {row.to ? <Link to={row.to} className={cls} style={style}>{body}</Link> : <div className={cls} style={style}>{body}</div>}
          </li>
        );
      })}
    </ul>
  );
};

/** Secondary cell. */
export const Muted: React.FC<{ children: React.ReactNode; mono?: boolean }> = ({ children, mono }) => (
  <span className={cn('text-[12px] text-[color:var(--oa-muted)]', mono && 'font-mono text-[11px]')}>{children}</span>
);

/** Right-aligned time cell. */
export const When: React.FC<{ children: React.ReactNode }> = ({ children }) => <span className="text-[11.5px] tabular-nums text-[color:var(--oa-muted)]">{children}</span>;

/** Small tinted label. */
export const Tag: React.FC<{ children: React.ReactNode; color?: string }> = ({ children, color = 'var(--oa-accent)' }) => (
  <span
    className="inline-flex items-center rounded-md px-1.5 py-0.5 text-[10.5px] font-semibold uppercase tracking-wide"
    style={{ background: `color-mix(in oklab, ${color} 14%, transparent)`, color }}
  >
    {children}
  </span>
);
